import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchShowCase } from "../../features/CaseReducer/showCaseReducer";
import Loading from "../Loading";
import { useTranslation } from "react-i18next";
import { DonateButton } from "../../Buttons/Buttons";
import { FormattedNumber } from "../../../utils/FormattedNumber";

function ShowCase() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { case: showCase, loading } = useSelector((state) => state.showCase);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    dispatch(fetchShowCase(id));
  }, [dispatch, id, i18n.language]);

  const progress = (showCase?.paid_amount / showCase?.amount * 100) >= 100 ? 100 : (showCase?.paid_amount / showCase?.amount * 100);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const progressVariants = {
    hidden: { width: 0 },
    visible: { width: `${progress}%` }
  };

  if (loading) return <Loading />;
  const totalRemaining =  showCase?.paid_amount>= showCase?.amount ? 0 : showCase?.amount - showCase?.paid_amount;
  return (
    <motion.div
      dir={i18n.language == "ckb" || i18n.language == "ar" ? "rtl" : "ltr"}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="max-w-4xl mx-auto px-4 py-8"
    >
      {/* Hero Image Section */}
      <motion.div variants={itemVariants} style={{ aspectRatio: "16/9" }} className="   flex justify-center rounded-md">
        <img
          src={process.env.REACT_APP_IMG_URL + showCase?.head_image}
          alt={showCase?.title}
          style={{ aspectRatio: "16/9" }}
          className="object-cover rounded-md shadow-md w-full "
        />

      </motion.div>

      {/* Progress Section */}
      <motion.div variants={itemVariants} className="mt-8 space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between text-lg font-medium">
            <span className="text-gray-600">{t("moneyNeed")}</span>
            <span className="text-[#068D1F]">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <motion.div
              className="bg-gradient-to-r from-[#068D1F] to-[#0EAE2C] h-3 rounded-full"
              variants={progressVariants}
              transition={{ type: "spring", duration: 1.5 }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-white border border-gray-400/50 border-gray-200 rounded-md">
            <p className="text-sm text-gray-500">{t("total")}</p>
            <motion.p
              className="text-3xl font-bold text-gray-800"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
            >

              {
                showCase?.is_show_currency ? <FormattedNumber
                  value={showCase?.amount || 0}
                  locale="en-US"
                  options={{ style: 'currency', currency: showCase?.currency?.name || 'IQD' }}
                /> : showCase?.amount?.split('.')[0]
              }

            </motion.p>

          </div>
          <div className="p-4 bg-white border border-gray-400/50 rounded-md">
            <p className="text-sm text-gray-500">{t("paidAmount")}</p>
            <motion.p
              className="text-3xl font-bold text-gray-800"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              {
                showCase?.is_show_currency ? <FormattedNumber
                  value={showCase?.paid_amount || 0}
                  locale="en-US"
                  options={{ style: 'currency', currency: showCase?.currency?.name || 'IQD' }}
                /> : showCase?.paid_amount
              }
              {/* <FormattedNumber
                value={showCase?.paid_amount || 0}
                locale="en-US"
                options={{ style: 'currency', currency: 'IQD' }}
              /> */}
            </motion.p>

          </div>
          <div className="p-4 bg-white border border-gray-400/50 rounded-md">
            <p className="text-sm text-gray-500">{t("Remaining")}</p>
            <motion.p
              className="text-3xl font-bold text-gray-800"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              {
                showCase?.is_show_currency ? <FormattedNumber
                  value={totalRemaining || 0}
                  locale="en-US"
                  options={{ style: 'currency', currency: showCase?.currency?.name || 'IQD' }}
                /> : totalRemaining
              }

            </motion.p>

          </div>
        </div>
      </motion.div>

      {/* Description Section */}
      <motion.div
        variants={itemVariants}
        className="mt-8 prose break-words max-w-none"
        dangerouslySetInnerHTML={{ __html: showCase?.description }}
      />

      {/* Donate Button */}
      <motion.div variants={itemVariants} className="mt-8 w-full ">
        <DonateButton isFull={true} center={true} />
      </motion.div>
    </motion.div>
  );
}

export default ShowCase;